import * as Sentry from "@sentry/react";

import { uid } from "utils";
import { getSentryRelease } from "utils/sentryRelease";

// sessionStorage survives a renderer crash-and-reload in the same tab, which is
// the only signal we have after Chrome kills the process ("Aw Snap"): the SDK is
// dead at crash time, so the *next* load reports on the previous session's behalf.
const STORAGE_KEY = "eva.crash-detector.v1";
const HEARTBEAT_INTERVAL_MS = 15_000;
// Last sampled JS heap at/above this fraction of Chrome's limit marks a detected
// crash as a likely OOM. The last sample (≤15s before death) reflects the state at
// crash time; the session peak is kept as context only, since a spike that GC
// recovered from long before an unrelated crash should not count as OOM evidence.
// performance.memory is quantized and Chrome-only — a confidence signal, not ground truth.
const LIKELY_OOM_HEAP_PCT = 0.9;
// A last heartbeat older than this at crash time means the tab was not actively
// beating when it died — background kill or session restore, not a foreground crash
const STALE_BEAT_MS = 60_000;
// window.name lives in the browser process, so it survives the renderer
// crash-and-reload — but window.open() and duplicated tabs COPY sessionStorage
// into a context with a different (or empty) name. Comparing the token stored in
// the record against window.name distinguishes a real crash-reload from inherited
// storage (popout charts, standalone dashboards, duplicated tabs).
const TAB_TOKEN_PREFIX = "eva-tab:";
// Breadcrumb trail persisted with the heartbeat so a crash report can say what
// the user was doing. Sentry's in-memory breadcrumbs die with the renderer.
const MAX_ACTIONS = 20;
const MAX_ACTION_MESSAGE_LENGTH = 160;
// Breadcrumbs can fire at high frequency (console, pointer events); cap
// sessionStorage writes — beat() and visibilitychange flush anything pending.
const ACTION_WRITE_THROTTLE_MS = 1_000;

interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

export interface HeapSample {
  usedJSHeapSize: number;
  jsHeapSizeLimit: number;
  pctOfLimit: number;
  sampledAt: number;
}

export interface ActionEntry {
  ts: number;
  category: string;
  message: string;
}

export interface CrashDetectorUser {
  id: string;
  organizationId?: Maybe<string>;
}

export interface HeartbeatRecord {
  cleanExit: boolean;
  startedAt: number;
  lastBeatAt: number;
  route: string;
  release: string;
  heap: Nullable<HeapSample>;
  peakHeapPct: number;
  tabToken: string;
  userId: Nullable<string>;
  organizationId: Nullable<string>;
  actions: ActionEntry[];
}

export interface CrashVerdict {
  likelyOom: boolean;
  record: HeartbeatRecord;
}

export function sampleHeap(): Nullable<HeapSample> {
  const memory = (performance as Performance & { memory?: PerformanceMemory }).memory;
  if (!memory || !memory.jsHeapSizeLimit) {
    return null;
  }
  return {
    usedJSHeapSize: memory.usedJSHeapSize,
    jsHeapSizeLimit: memory.jsHeapSizeLimit,
    pctOfLimit: memory.usedJSHeapSize / memory.jsHeapSizeLimit,
    sampledAt: Date.now()
  };
}

export function evaluatePreviousSession(
  prev: Maybe<HeartbeatRecord>
): Nullable<CrashVerdict> {
  if (!prev || prev.cleanExit) {
    return null;
  }
  return {
    likelyOom: (prev.heap?.pctOfLimit ?? 0) >= LIKELY_OOM_HEAP_PCT,
    record: prev
  };
}

// True when the record was copied into this tab (window.open, tab duplication)
// rather than written by a previous session of this same tab
export function isInheritedRecord(
  prev: Maybe<HeartbeatRecord>,
  currentTabToken: string
): boolean {
  return !!prev && !!prev.tabToken && prev.tabToken !== currentTabToken;
}

// Replaces id-like path segments so the Sentry tag stays low-cardinality;
// the raw route is preserved in the event context
export function normalizeRoute(route: string): string {
  return (
    route
      .split("/")
      .map((segment) => (segment === "" || /^[a-z-]+$/i.test(segment) ? segment : ":id"))
      .join("/") || "/"
  );
}

function ensureTabToken(): string {
  // Only a random token — window.name is readable after cross-origin navigation,
  // so nothing sensitive may ever go here
  if (!window.name.startsWith(TAB_TOKEN_PREFIX)) {
    window.name = `${TAB_TOKEN_PREFIX}${uid()}`;
  }
  return window.name;
}

function readRecord(): Nullable<HeartbeatRecord> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    return parsed as HeartbeatRecord;
  } catch {
    return null;
  }
}

function writeRecord(record: HeartbeatRecord): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage may be unavailable (private mode, quota) — detection degrades to a no-op.
  }
}

function wasTabDiscarded(): boolean {
  return (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true;
}

function reportCrash(verdict: CrashVerdict): void {
  const { record } = verdict;
  const msSinceLastBeat = Date.now() - record.lastBeatAt;
  Sentry.withScope((scope) => {
    scope.setLevel("error");
    // One Sentry issue for all detected crashes; slice by tags/release in the UI
    scope.setFingerprint(["renderer-crash-detected"]);
    // Attribute the event to whoever was logged in when the tab died — the new
    // session hasn't loaded its user yet at this point (and may never log in)
    if (record.userId) {
      scope.setUser({ id: record.userId });
    }
    if (record.organizationId) {
      scope.setTag("crash.prev_org", String(record.organizationId));
    }
    scope.setTag("crash.detected", "true");
    scope.setTag("crash.likely_oom", String(verdict.likelyOom));
    scope.setTag("crash.stale_beat", String(msSinceLastBeat > STALE_BEAT_MS));
    scope.setTag("crash.prev_release", record.release);
    scope.setTag("crash.prev_route", normalizeRoute(record.route));
    scope.setContext("previous_session", {
      route: record.route,
      release: record.release,
      userId: record.userId,
      organizationId: record.organizationId,
      startedAt: new Date(record.startedAt).toISOString(),
      lastBeatAt: new Date(record.lastBeatAt).toISOString(),
      sessionDurationMs: record.lastBeatAt - record.startedAt,
      msSinceLastBeat,
      peakHeapPct: record.peakHeapPct,
      lastHeap: record.heap,
      // Most recent last — the tail is what the user did right before the crash
      actions: (record.actions ?? []).map(
        (a) => `${new Date(a.ts).toISOString()} [${a.category}] ${a.message}`
      )
    });
    Sentry.captureMessage("Renderer crash detected in previous session", "error");
  });
}

let current: Nullable<HeartbeatRecord> = null;

function beat(): void {
  if (!current) {
    return;
  }
  const heap = sampleHeap();
  current.lastBeatAt = Date.now();
  if (heap) {
    current.heap = heap;
    current.peakHeapPct = Math.max(current.peakHeapPct, heap.pctOfLimit);
  }
  writeRecord(current);
}

// Called on route changes so a crash report can say what the user was viewing
export function noteCrashDetectorRoute(route: string): void {
  if (!current || current.route === route) {
    return;
  }
  current.route = route;
  beat();
}

// Called when Sentry's user context is set/cleared so a crash report can be
// attributed to whoever was logged in when the tab died
export function noteCrashDetectorUser(user: Nullable<CrashDetectorUser>): void {
  if (!current) {
    return;
  }
  current.userId = user?.id ?? null;
  current.organizationId = user?.organizationId ?? null;
  writeRecord(current);
}

let lastActionWriteAt = 0;

// Mirrors Sentry breadcrumbs (clicks, navigation, API calls) into the
// heartbeat record — the in-memory trail dies with the renderer, this copy
// survives to describe what the user was doing before a crash
export function noteCrashDetectorBreadcrumb(breadcrumb: {
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
}): void {
  const category = breadcrumb.category ?? "default";
  // Skip Sentry-internal breadcrumbs and our own session markers
  if (!current || category.startsWith("sentry") || category === "session") {
    return;
  }

  const { data } = breadcrumb;
  const message =
    breadcrumb.message ||
    // http breadcrumbs carry their info in data, not message
    [data?.method, data?.url, data?.status_code].filter(Boolean).join(" ");
  if (!message) {
    return;
  }

  current.actions.push({
    ts: Date.now(),
    category,
    message: String(message).slice(0, MAX_ACTION_MESSAGE_LENGTH)
  });
  if (current.actions.length > MAX_ACTIONS) {
    current.actions.splice(0, current.actions.length - MAX_ACTIONS);
  }

  // Throttled write-through; beat()/visibilitychange flush anything pending
  const now = Date.now();
  if (now - lastActionWriteAt >= ACTION_WRITE_THROTTLE_MS) {
    lastActionWriteAt = now;
    writeRecord(current);
  }
}

const noopTeardown = (): void => undefined;

// Returns a teardown function (used by tests; the app runs it for the page lifetime)
export function initCrashDetector(): () => void {
  if (current || !Sentry.isInitialized()) {
    return noopTeardown;
  }

  const tabToken = ensureTabToken();
  const prev = readRecord();
  const inherited = isInheritedRecord(prev, tabToken);
  const verdict = inherited ? null : evaluatePreviousSession(prev);
  if (inherited) {
    Sentry.addBreadcrumb({
      category: "session",
      message: "Session storage inherited from another tab",
      level: "info"
    });
  }
  if (verdict) {
    if (wasTabDiscarded()) {
      // Chrome memory-saver discard, not a crash — reloading is expected behavior.
      // Still worth a breadcrumb: discards are a memory-pressure signal.
      Sentry.addBreadcrumb({
        category: "session",
        message: "Previous session ended by tab discard",
        level: "info",
        data: { peakHeapPct: verdict.record.peakHeapPct }
      });
    } else {
      reportCrash(verdict);
    }
  }

  const now = Date.now();
  const heap = sampleHeap();
  current = {
    cleanExit: false,
    startedAt: now,
    lastBeatAt: now,
    route: window.location.pathname,
    release: getSentryRelease(),
    heap,
    peakHeapPct: heap?.pctOfLimit ?? 0,
    tabToken,
    userId: null,
    organizationId: null,
    actions: []
  };
  writeRecord(current);

  const intervalId = window.setInterval(beat, HEARTBEAT_INTERVAL_MS);

  const onPageHide = () => {
    if (current) {
      current.cleanExit = true;
      writeRecord(current);
    }
  };
  // A bfcache restore resumes the session after pagehide marked it clean
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted && current) {
      current.cleanExit = false;
      beat();
    }
  };
  // Last chance to capture state before a backgrounded tab is killed
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      beat();
    }
  };

  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    window.clearInterval(intervalId);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    current = null;
  };
}
